import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/lib/tracking";

/**
 * Featured Platforms panel — Adobe Marketo Engage + Salesforce.
 *
 * Brand marks below use the authentic vector paths published by
 * simple-icons (CC0 / brand-standard glyphs):
 *  - Adobe: the iconic stylised "A" inside a red 5% rounded square.
 *  - Salesforce: the official blue cloud silhouette.
 *
 * Trademark note: marks belong to Adobe Inc. and Salesforce, Inc.
 * Used here only to indicate platform capability — Xadentra is independent
 * and not affiliated with or endorsed by either company.
 */

const AdobeMark = ({ className = "" }) => (
  // Red rounded square wrapper + authentic Adobe "A" glyph (simple-icons path).
  // The inner A is drawn in white at 24x24, transformed to sit centered with breathing room.
  <svg viewBox="0 0 32 32" className={className} aria-label="Adobe Marketo Engage logo">
    <rect width="32" height="32" rx="5" fill="#FA0F00" />
    <g transform="translate(4 4) scale(1)">
      <path
        fill="#fff"
        d="M13.966 22.624l-1.69-4.281H8.122l3.892-9.144l5.662 13.425zM8.884 1.376H0v21.248zm15.116 0h-8.884L24 22.624Z"
      />
    </g>
  </svg>
);

const SalesforceMark = ({ className = "" }) => (
  // Official cloud silhouette (simple-icons path), Salesforce brand blue.
  <svg viewBox="0 0 24 24" className={className} aria-label="Salesforce logo">
    <path
      fill="#00A1E0"
      d="M10.006 5.415a4.2 4.2 0 0 1 3.045-1.306c1.56 0 2.954.9 3.69 2.205c.63-.3 1.35-.45 2.1-.45c2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 0 1-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.31 4.31 0 0 1 8.88 20.4a4.3 4.3 0 0 1-4.05-2.82c-.27.062-.54.076-.825.076c-2.204 0-4.005-1.8-4.005-4.05c0-1.5.811-2.805 2.01-3.51c-.255-.57-.39-1.2-.39-1.846c0-2.58 2.1-4.65 4.65-4.65c1.53 0 2.85.705 3.72 1.8"
    />
  </svg>
);

const PLATFORMS = [
  {
    Mark: AdobeMark,
    name: "Adobe Marketo Engage",
    role: "Marketing automation platform",
    bullets: ["Programs & lifecycle", "New Email Designer", "MCP Server enablement"],
    cta: { label: "Talk to a Marketo specialist", to: "/contact?focus=marketo", focus: "marketo" },
  },
  {
    Mark: SalesforceMark,
    name: "Salesforce",
    role: "CRM & revenue platform",
    bullets: ["Lead routing & MQL workflows", "Campaign sync & attribution", "Flow / Apex governance"],
    cta: { label: "Talk to a Salesforce specialist", to: "/contact?focus=salesforce", focus: "salesforce" },
  },
];

export default function BrandLogos({ variant = "dark" }) {
  const isDark = variant === "dark";
  return (
    <section
      data-testid="brand-logos-section"
      className={`relative overflow-hidden ${
        isDark
          ? "bg-[#07111F] text-white border-t border-white/10"
          : "bg-[#F7F9FC] text-[#07111F] border-t border-[#E5E7EB]"
      }`}
    >
      {isDark && (
        <>
          <div
            className="xa-blob pointer-events-none"
            style={{ width: 420, height: 420, background: "#6D4DFF", top: -160, left: "20%", opacity: 0.28 }}
          />
          <div className="absolute inset-0 xa-grid-dark opacity-40 pointer-events-none" />
        </>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-12">
          <div>
            <div
              className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
                isDark ? "text-[#00D4FF]" : "text-[#6D4DFF]"
              }`}
            >
              Featured platforms
            </div>
            <h2
              className={`font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl ${
                isDark ? "text-white" : "text-[#07111F]"
              }`}
            >
              Built deep on Adobe Marketo Engage and Salesforce.
            </h2>
          </div>
          <p
            className={`max-w-md text-sm leading-relaxed ${
              isDark ? "text-white/65" : "text-[#6B7280]"
            }`}
          >
            Two platforms we know end-to-end — implementation, integration,
            day-to-day operations, and the messy edges where Adobe Marketo
            Engage and Salesforce actually meet.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {PLATFORMS.map(({ Mark, name, role, bullets, cta }) => (
            <div
              key={name}
              data-testid={`brand-card-${cta.focus}`}
              className={`group relative rounded-2xl p-7 lg:p-8 xa-lift overflow-hidden flex flex-col ${
                isDark
                  ? "bg-white/[0.03] border border-white/10 hover:border-white/25"
                  : "bg-white border border-[#E5E7EB] hover:border-[#07111F]"
              }`}
            >
              <div
                aria-hidden
                className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: "radial-gradient(closest-side, rgba(109,77,255,0.22), transparent)",
                }}
              />

              <div className="relative flex items-start gap-5">
                <div
                  className={`shrink-0 grid place-items-center rounded-xl ${
                    isDark
                      ? "bg-white/5 border border-white/10"
                      : "bg-[#F7F9FC] border border-[#E5E7EB]"
                  } w-16 h-16 lg:w-[72px] lg:h-[72px] p-2.5`}
                >
                  <Mark className="w-full h-full" />
                </div>
                <div className="min-w-0">
                  <div
                    className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
                      isDark ? "text-white/45" : "text-[#6B7280]"
                    }`}
                  >
                    {role}
                  </div>
                  <h3
                    className={`font-display mt-2 text-xl lg:text-2xl font-semibold tracking-tight ${
                      isDark ? "text-white" : "text-[#07111F]"
                    }`}
                  >
                    {name}
                  </h3>
                </div>
              </div>

              <ul
                className={`mt-6 grid gap-2.5 text-sm flex-1 ${
                  isDark ? "text-white/80" : "text-[#07111F]"
                }`}
              >
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact#booking-section"
                onClick={() => trackEvent("brand_card_cta_click", { platform: cta.focus })}
                data-testid={`brand-cta-${cta.focus}`}
                className={`mt-7 inline-flex items-center justify-between gap-3 rounded-full pl-5 pr-2 py-2 text-sm font-medium transition group/cta ${
                  isDark
                    ? "bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 hover:border-white/30 text-white"
                    : "bg-[#07111F] hover:bg-[#1a2540] text-white"
                }`}
              >
                <span>{cta.label}</span>
                <span
                  className={`grid place-items-center w-7 h-7 rounded-full ${
                    isDark
                      ? "bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] text-white"
                      : "bg-gradient-to-br from-[#6D4DFF] to-[#00D4FF] text-white"
                  } transition-transform group-hover/cta:translate-x-0.5`}
                >
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>
          ))}
        </div>

        <p
          className={`mt-8 text-[11px] font-mono uppercase tracking-[0.18em] ${
            isDark ? "text-white/35" : "text-[#6B7280]"
          }`}
        >
          Brand marks belong to Adobe Inc. and Salesforce, Inc. Xadentra is an
          independent agency — not affiliated with or endorsed by either company.
        </p>
      </div>
    </section>
  );
}
